'use client'

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { toast } from "@/components/ui/toast"
import { MESSAGES } from "@/components/ui/toast-messages"

export interface SearchResult {
  document_id: string;
  score: number;
  metadata: {
    document_id: string;
    document_date: string;
    document_type: string;
    case_name: string;
    court: string;
    ecli: string;
    celex: string;
    citation: string;
  };
  summary: string;
  explanation: string;
  paragraphs: Paragraph[];
  connections: any;
}

interface Paragraph {
  paragraph_id: string;
  score: number;
  text: string
}

interface Filters {
  tags: string[];
  document_authors: string[];
  document_types: string[];
  start_date: string | null;
  end_date: string | null;
}

interface SearchResponse {
  searchId: number;
  answer: Answer[];
  results: SearchResult[];
}

export interface Answer {
  text: string,
  sources: Source[];
}

export interface Source {
  document_id: string;
  index: number;
  citation: string;
}

export interface Tag {
  code: string;
  label: string;
  score: string;
}

interface SearchContextType {
  input: string;
  setInput: (input: string) => void;
  query: string;
  setQuery: (query: string) => void;
  answer: Answer[];
  setAnswer: (answer: Answer[]) => void;
  isTyping: boolean;
  setIsTyping: (isTyping: boolean) => void;
  results: SearchResult[];
  setResults: (results: SearchResult[]) => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  suggestions: Tag[];
  setSuggestions: (suggestions: Tag[]) => void;
  searchId: number | undefined;
  setSearchId: (searchId: number) => void;
  loading: boolean;
  messages: string[];
  executeSearch: (inputQuery: string) => Promise<SearchResponse | null>;
  updateFiltersFromTags: (tags: string[]) => void;
  cancelSearch: () => void;
}

const SearchContext = createContext<SearchContextType>({} as SearchContextType)

const AUTHOR_TYPES = ["Court of Justice", "General Court"];
const DOCUMENT_TYPES = ["judgment", "order", "opinion", "directive", "regulation", "decision", "treaty"];

function mapDocumentTypes(
  inputList: string[],
  mapping: Record<string, string>
): string[] {
  return inputList.map((item: string): string => {
    // Returns the mapped value if found, otherwise returns the original item
    return mapping[item] ?? item;
  });
}

// --- Usage with your Supabase Setup ---

const typeMapping: Record<string, string> = {
  "judgments": "judgment",
  "orders": "order",
  "opinions": "opinion",
  "directives": "directive",
  "regulations": "regulation",
  "decisions": "decision",
  "treaties": "treaty",
};

const STORAGE_KEY = "search_state_v1";

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [input, setInput] = useState("")
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [messages, setMessages] = useState<string[]>([]);
  const [searchId, setSearchId] = useState<number>();
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [answer, setAnswer] = useState<Answer[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [filters, setFilters] = useState<Filters>({
    tags: [],
    document_authors: [],
    document_types: [],
    start_date: null,
    end_date: null,
  });

  // 1. HYDRATION: Load data from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setQuery(parsed.query || "");
        setResults(parsed.results || []);
        setAnswer(parsed.answer || "");
        setMessages(parsed.messages || "");
        setFilters(parsed.filters || { tags: [], document_authors: [], document_types: [], start_date: null, end_date: null });
        setSearchId(parsed.searchId);
      } catch (e) {
        console.error("Failed to parse search persistence", e);
      }
    }
  }, []);

  // 2. PERSISTENCE: Save to localStorage whenever important values change
  useEffect(() => {
    // We don't want to save 'loading' or 'messages' usually, 
    // just the core data needed to restore the view.
    const stateToSave = {
      query,
      results,
      answer,
      filters,
      searchId,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [query, results, answer, filters, searchId]);

  const updateFiltersFromTags = useCallback((tags: string[]) => {

    tags = mapDocumentTypes(tags, typeMapping)

    setFilters({
      // Everything stays in tags
      tags: tags,

      // Specifically extract courts
      document_authors: tags.filter(label =>
        AUTHOR_TYPES.includes(label)
      ),

      // Specifically extract document types
      document_types: tags.filter(label =>
        DOCUMENT_TYPES.includes(label)
      ),

      start_date: null,
      end_date: null,
    });
  }, []);

  const executeSearch = async (inputQuery: string): Promise<SearchResponse | null> => {
    if (!inputQuery.trim()) {
      toast(MESSAGES.search.missingQuery);
      return null;
    }

    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const { signal } = controller;

    setQuery(inputQuery)
    setLoading(true);
    setMessages([]); // Reset logs for a new search session
    setResults([]); // Optional: clear old results

    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;

    try {
      const response = await fetch("/api/streaming", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal,
        body: JSON.stringify({
          query: inputQuery,
          filters: filters
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Search failed");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let finalData: SearchResponse | null = null;
      let buffer = "";

      if (!reader) throw new Error("ReadableStream not supported.");

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        // 2. Accumulate the chunk into the buffer
        buffer += decoder.decode(value, { stream: true });

        // 3. Look for the SSE delimiter in the accumulated buffer
        let boundary = buffer.indexOf("\n\n");

        while (boundary !== -1) {
          // Extract the full message and update buffer
          const completeLine = buffer.slice(0, boundary).trim();
          buffer = buffer.slice(boundary + 2);

          if (completeLine.startsWith("data: ")) {
            try {
              const jsonStr = completeLine.replace("data: ", "");
              const payload = JSON.parse(jsonStr);

              // 1. Handle Status Messages (The Log)
              if (payload.status && payload.status !== "complete") {
                setMessages((prev) => {
                  if (prev[prev.length - 1] === payload.status) return prev;
                  return [...prev, payload.status];
                });
              }

              // 2. Handle Final Data
              if (payload.status === "complete") {
                console.log(payload.result.recommendations)
                finalData = {
                  searchId: payload.searchId,
                  results: payload.result.recommendations,
                  answer: payload.result.answer,
                };
                setMessages((prev) => [...prev, "Done!"]);
                setResults(finalData.results);
                setSearchId(finalData.searchId);
                setAnswer(finalData.answer);
              }

              // 3. Handle FastAPI Validation Errors
              if (payload.detail) {
                throw new Error(payload.detail[0]?.msg || "ML Validation Error");
              }
            } catch (e) {
              console.error("Parse Error:", e);
            }
          }
          // Check for the next message in the remaining buffer
          boundary = buffer.indexOf("\n\n");
        }
      }
      return finalData;

    } catch (error: any) {
      setMessages((prev) => [...prev, `Error: ${error.message}`]);
      // toast.error("Search Failed", { description: error.message });
      toast(MESSAGES.search.failed());
      return null;
    } finally {
      reader?.releaseLock();
      setLoading(false);
      abortControllerRef.current = null;
    }
  }

  useEffect(() => {
    if (!input || input.trim().length < 5) {
      setSuggestions([]);
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    const handler = setTimeout(async () => {
try {
      const res = await fetch("/api/suggest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_text: input }),
      });
        const data = await res.json();
        setSuggestions(data);
      } finally {
        setIsTyping(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [input]);

  const cancelSearch = () => {
    abortControllerRef.current?.abort();
    setLoading(false);
    setMessages([]);
    setQuery("")
  };

  return (
    <SearchContext.Provider value={{ query, setQuery, results, setResults, answer, setAnswer, loading, messages, executeSearch, suggestions, setSuggestions, filters, setFilters, searchId, setSearchId, updateFiltersFromTags, isTyping, setIsTyping, cancelSearch, input, setInput }}>
      {children}
    </SearchContext.Provider>
  )
}

export const useSearch = () => useContext(SearchContext)
